import { useCallback, useEffect, useState } from 'react';
import { Button, Icon, Spinner, Text } from '@nimbus-ds/components';
import { ChevronLeftIcon } from '@nimbus-ds/icons';
import { SideModal } from '@nimbus-ds/patterns';
import {
  LocationPriorityRequestDto,
  LocationResponseDto,
} from '@tiendanube/common';
import { Stack } from 'commons/components';
import { useAsyncFunc } from 'commons/hooks';
import { useLocations } from 'domains/Shipping/Locations/hooks';
import useLocationListToast from 'domains/Shipping/Locations/hooks/useLocationListToast';
import LocationsSortableList from 'domains/Shipping/Locations/pages/LocationListPage/components/LocationSortableList';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';

interface LocationsPriorityModalProps {
  onClose: (type: 'save' | 'close') => void;
}

function LocationsPriorityModal({
  onClose,
}: Readonly<LocationsPriorityModalProps>) {
  const t = useTranslationShipping('locations');
  const { locations, updateLocationsOrdering, isLoading } = useLocations();
  const { onSuccess, onError } = useLocationListToast();
  const [locationsList, setLocationsList] = useState<LocationResponseDto[]>([]);

  const handleOnChange = useCallback((newOrder: LocationResponseDto[]) => {
    const newLocationsList = newOrder.map((location, index) => ({
      ...location,
      priority: index,
      main: index === 0,
    }));
    setLocationsList(newLocationsList);
  }, []);

  const [handleUpdateLocationsOrdering, isSavingOrdering] = useAsyncFunc(
    async () => {
      const newLocationsOrdering = locationsList.map(
        ({ id, priority }) =>
          ({
            locationId: id,
            priority: priority,
          } as LocationPriorityRequestDto),
      );
      await updateLocationsOrdering(newLocationsOrdering);
    },
    () => {
      onClose('save');
      onSuccess('sort');
    },
    () => onError('sort'),
  );

  useEffect(() => {
    setLocationsList(locations);
  }, [locations]);

  return (
    <SideModal
      open
      onRemove={() => onClose('close')}
      maxWidth={{ xs: '100%', md: '45%', lg: '500px' }}
      padding="base"
      paddingHeader="none"
      title={t('sort.title')}
      footer={{
        secondaryAction: {
          children: (
            <>
              {isSavingOrdering && <Spinner size="small" />}
              {t('form.edit.save')}
            </>
          ),
          onClick: handleUpdateLocationsOrdering,
          disabled: isSavingOrdering,
          appearance: 'primary',
        },
        primaryAction: {
          children: t('form.edit.cancel'),
          onClick: () => onClose('close'),
          disabled: isSavingOrdering,
        },
      }}
      headerAction={
        <Button appearance="transparent" onClick={() => onClose('close')}>
          <Icon color="primary-textHigh" source={<ChevronLeftIcon />} />
        </Button>
      }
    >
      <Stack column spacing="base" align="flex-start">
        <Text fontSize="base">{t('sort.subtitle')}</Text>
        <LocationsSortableList
          locationsList={isLoading ? [] : locationsList}
          handleOnChange={handleOnChange}
          showDetails={false}
          isLoading={isLoading ?? locationsList.length === 0}
        />
      </Stack>
    </SideModal>
  );
}

export default LocationsPriorityModal;
